import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, XIcon, ShieldCheckIcon } from 'lucide-react';
function VimeoPlayer({
  isFloating,
  onDismiss
}: {
  isFloating: boolean;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  return <div className={`relative z-10 w-full rounded-lg shadow-2xl overflow-hidden transition-all duration-500 ease-in-out ${isFloating ? 'fixed bottom-4 right-4 !w-[300px] md:!w-[380px] z-[90] rounded-xl border border-gold/30' : ''}`} style={isFloating ? {
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
  } : {}} data-id="element-206">
      <div style={{
      padding: '56.25% 0 0 0',
      position: 'relative'
    }} data-id="element-207">
        <iframe src="https://player.vimeo.com/video/1177040592?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      }} title="AI Web Closer VSL" data-id="element-208" />
      </div>
      <AnimatePresence data-id="element-209">
        {isFloating && <motion.button initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.5
      }} transition={{
        duration: 0.2
      }} onClick={onDismiss} className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-black/70 hover:bg-black/90 border border-white/20 transition-colors" aria-label="Cerrar video flotante" data-id="element-210">
            <XIcon className="h-3.5 w-3.5 text-white" data-id="element-211" />
          </motion.button>}
      </AnimatePresence>
    </div>;
}
export function HeroLP4() {
  const [isFloating, setIsFloating] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const videoSentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = videoSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        setIsFloating(true);
      } else {
        setIsFloating(false);
        setIsDismissed(false);
      }
    }, {
      threshold: 0.1
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);
  const handleDismiss = () => {
    setIsDismissed(true);
  };
  return <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden px-4 pt-10 pb-20" data-id="element-212">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-light to-navy-dark" data-id="element-213">
        <motion.div animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-0 right-0 w-96 h-96 bg-gold/20 rounded-full blur-3xl" data-id="element-214" />
        <motion.div animate={{
        scale: [1.2, 1, 1.2],
        opacity: [0.2, 0.4, 0.2]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-0 left-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl" data-id="element-215" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto" data-id="element-216">
        {/* Headline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="text-center mb-8" data-id="element-217">
          <p className="font-body text-sm md:text-base text-gold font-bold uppercase tracking-wider mb-4" data-id="element-218">
            Para emprendedores y principiantes que quieren generar ingresos
            reales en 2026:
          </p>

          <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl leading-tight mb-4" data-id="element-219">
            Cómo Generar Tus Primeros <span className="text-gold" data-id="element-220">$1,000</span>{' '}
            Esta Semana Vendiendo Sitios Web a Negocios Locales...
          </h1>

          <p className="font-body text-lg md:text-xl text-gray-300 mb-6 italic" data-id="element-221">
            (Incluso si no sabes nada de diseño, programación o ventas)
          </p>

          <div className="bg-navy-dark/50 border border-gold/20 rounded-xl p-4 md:p-6 max-w-3xl mx-auto mb-8" data-id="element-222">
            <p className="font-body text-base md:text-lg text-gray-200" data-id="element-223">
              Descubre el método de la{' '}
              <span className="text-gold font-bold" data-id="element-224">
                "Demo que Cierra Sola"
              </span>
              : La Inteligencia Artificial encuentra a los clientes y construye
              los sitios web en minutos. Tú solo haces la llamada y cobras.
            </p>
          </div>
        </motion.div>

        {/* Hero Video */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="relative mb-10" data-id="element-225">
          <div ref={videoSentinelRef} className="absolute inset-0 pointer-events-none" data-id="element-226" />
          <VimeoPlayer isFloating={!isDismissed && isFloating} onDismiss={handleDismiss} data-id="element-227" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-transparent rounded-lg pointer-events-none" data-id="element-228" />
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 1
      }} className="text-center" data-id="element-229">
          <div className="flex flex-col items-center justify-center gap-4" data-id="element-230">
            <motion.a href="/checkout-2" onClick={() => {
            if ((window as any).fbq) (window as any).fbq('track', 'AddToCart', {
              content_name: 'AI Web Closer Course',
              content_type: 'product',
              value: 27,
              currency: 'USD'
            });
          }} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="group w-full sm:w-auto bg-gold hover:bg-gold-light text-navy font-heading font-black text-xl px-8 py-5 rounded-xl shadow-xl shadow-gold/20 transition-all duration-300 flex items-center justify-center gap-3" data-id="element-231">
              QUIERO ACCESO POR $27
              <ArrowRightIcon className="h-6 w-6 group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-232" />
            </motion.a>

            <div className="flex items-center gap-2 text-gray-400 font-body text-sm" data-id="element-233">
              <ShieldCheckIcon className="h-4 w-4 text-gold" data-id="element-234" />
              <span data-id="element-235">Garantía de 30 Días Sin Preguntas.</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}